import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import MultiSelectDropdown from '../../../components/multi-check-dropdown';
import type { SectionId } from './customize-layout-provider.d';
import { SECTION_IDS, useCustomizeLayoutContext } from './customize-layout-provider';
import shipmentDetailsMessages from '../fulfillment-plan/sections/shipment-details/messages';
import shipmentItemsMessages from '../fulfillment-plan/sections/shipment-items/messages';
import customsMessages from '../fulfillment-plan/sections/customs-information/messages';
import notesMessages from '../fulfillment-plan/sections/notes/messages';
import customerCommsMessages from '../fulfillment-plan/sections/customer-communication/messages';
import shipmentActivityMessages from '../fulfillment-plan/sections/shipment-activity/messages';
import configureShipmentMessages from '../fulfillment-plan/sections/configure-shipment/messages';
import { messages as osoMessages } from '../../other-shipping-options/component';
import { messages as fraudIndicatorsMessages } from '../fraud-indicators/messages';

import { messages } from './messages';
import style from './section-visibility-selector.module.less';

type SectionOption = {
  component: ReactNode;
  id: SectionId;
  value: string;
};

const SECTION_OPTIONS: SectionOption[] = [
  {
    id: SECTION_IDS.fraudIndicators,
    value: SECTION_IDS.fraudIndicators,
    component: <FormattedMessage {...fraudIndicatorsMessages.sectionAriaLabel} />,
  },
  {
    id: SECTION_IDS.shipmentDetails,
    value: SECTION_IDS.shipmentDetails,
    component: <FormattedMessage {...shipmentDetailsMessages.sectionTitle} />,
  },
  {
    id: SECTION_IDS.items,
    value: SECTION_IDS.items,
    component: <FormattedMessage {...shipmentItemsMessages.sectionTitle} />,
  },
  {
    id: SECTION_IDS.customs,
    value: SECTION_IDS.customs,
    component: <FormattedMessage {...customsMessages.customsHeader} />,
  },
  {
    id: SECTION_IDS.notes,
    value: SECTION_IDS.notes,
    component: <FormattedMessage {...notesMessages.notesSectionTitle} />,
  },
  {
    id: SECTION_IDS.customerComms,
    value: SECTION_IDS.customerComms,
    component: <FormattedMessage {...customerCommsMessages.templatesSectionTitle} />,
  },
  {
    id: SECTION_IDS.activity,
    value: SECTION_IDS.activity,
    component: <FormattedMessage {...shipmentActivityMessages.shipmentActivitySectionTitle} />,
  },
  {
    id: SECTION_IDS.rateCardReadOnly,
    value: SECTION_IDS.rateCardReadOnly,
    component: <FormattedMessage {...messages.rateCardReadOnlySectionTitle} />,
  },
  {
    id: SECTION_IDS.csw,
    value: SECTION_IDS.csw,
    component: <FormattedMessage {...configureShipmentMessages.sectionTitle} />,
  },
  {
    id: SECTION_IDS.oso,
    value: SECTION_IDS.oso,
    component: <FormattedMessage {...osoMessages.title} />,
  },
];

export const SectionVisibilitySelector = () => {
  const { formatMessage } = useIntl();
  const { availableSectionIds, onSectionVisibleToggled, visibleSections } = useCustomizeLayoutContext();

  const availableOptions = useMemo(() => {
    return SECTION_OPTIONS.filter((option) => availableSectionIds.includes(option.id as SectionId));
  }, [availableSectionIds]);

  const selectedOptions = useMemo(() => {
    return availableOptions.filter((option) => visibleSections.includes(option.id));
  }, [availableOptions, visibleSections]);

  const onOptionClicked = useCallback(
    (option: SectionOption, isChecked: boolean) => {
      onSectionVisibleToggled(option.id, isChecked);
    },
    [onSectionVisibleToggled],
  );

  const getLabel = () => {
    if (selectedOptions.length === 0) {
      return formatMessage(messages.noSections);
    }

    if (selectedOptions.length === availableOptions.length) {
      return formatMessage(messages.allSections);
    }

    return formatMessage(messages.sectionCount, {
      selectedCount: selectedOptions.length,
      totalCount: availableOptions.length,
    });
  };

  const renderItemLabel = useCallback((item: SectionOption) => item.component, []);

  return (
    <div className={style['section-visibility-selector-wrapper']}>
      <MultiSelectDropdown
        itemKeyName='id'
        items={availableOptions}
        onItemToggled={onOptionClicked}
        renderItemLabel={renderItemLabel}
        selectedItems={selectedOptions}
        toggleLabel={getLabel()}
        togglerClassName={style['dropdown-toggler']}
      />
    </div>
  );
};
